import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  SEPA_INVOICE_CHANGE_ROUTE,
  CLEARING_ACCOUNT_INFORMATION_REQUEST_ACCOUNT_SELECTION_ROUTE,
  CLEARING_ACCOUNT_INFORMATION_REQUEST_ROUTE,
  CUSTOMER_DATA_CHANGE_ROUTE,
  CUSTOMER_DATA_CHANGE_SELECTION_ROUTE,
  ENVIRONMENT_SERVICE_TOKEN,
  GOODIE_ROUTE,
  INTERACTIVE_INVOICE_DETAILS_ROUTE,
  INTERACTIVE_INVOICE_ROUTE,
  INVOICE_ADDRESS_CHANGE_ROUTE,
  INVOICE_PARTIALPAYMENT_OVERVIEW_ROUTE,
  LOCATION_TOKEN,
  MOVE_OUT_ADDRESS_SELECTION_ROUTE,
  MOVE_OUT_ROUTE,
  PDF_INVOICE_CHANGE_ROUTE,
  PRODUCTS_BILLINFO_ROUTE,
  PRODUCTS_CONSUMPTION,
  PRODUCTS_DETAILS_ROUTE,
  PRODUCTS_ROUTE,
  PROFILE_NEWSLETTER_ROUTE,
  PROFILE_ROUTE,
  RELOCATION_CURRENT_ADDRESS_ROUTE,
  RELOCATION_ROUTE,
  SECURE_OFFER_ROUTE,
  TARIFF_CHANGE_ADDRESS_SELECTION_ROUTE,
  TARIFF_CHANGE_ROUTE,
  UNPAID_INVOICES_PAYMENT_OPTIONS_ROUTE,
  UNPAID_INVOICES_ROUTE,
  UNPAID_INVOICES_SELECTION_ROUTE,
  VOUCHER_REDEEM_FORM_ROUTE,
  VOUCHER_REDEEM_ROUTE,
  PDF_INVOICE_CHANGE_ADDRESS_SELECTION_ROUTE,
  SEPA_INVOICE_CHANGE_ADDRESS_SELECTION_ROUTE,
  FEATURE_TOGGLED_OUT_ROUTE,
  INVOICE_ADDRESS_CHANGE_ADDRESS_SELECTION_ROUTE,
} from '@mwe/constants';
import { NavigationStateService } from '../cache/navigation-state.service';
import { FindPaymentSolutionStateService } from '../invoices/find-payment-solution/find-payment-solution-state.service';
import { InteractiveInvoicesStateService } from '../invoices/interactive-invoices/interactive-invoices-state.service';
import { EnvironmentServiceInterface } from '@mwe/environment';
import { AppStorageService } from '../cache/app-storage.service';

@Injectable({ providedIn: 'root' })
export class NavigationLogic {
  private navigationStateService = inject(NavigationStateService);
  private interactiveInvoicesStateService = inject(InteractiveInvoicesStateService);
  private findPaymentSolutionStateService = inject(FindPaymentSolutionStateService);
  private router = inject(Router);
  private appStorageService = inject(AppStorageService);
  private environmentService = inject<EnvironmentServiceInterface>(ENVIRONMENT_SERVICE_TOKEN);
  private location = inject<Location>(LOCATION_TOKEN);

  closeProcess(): Promise<boolean> {
    return this.router.navigateByUrl(this.navigationStateService.wizardCancelUrl);
  }

  navigateToLatestNavigatingBackItem(): void {
    const route = this.getNavigateBackRouteAndRemoveFromState();
    this.router.navigateByUrl(route);
  }

  getNavigateBackRouteAndRemoveFromState(): string {
    const backRoute = this.getRouteFromLatestNavigatingBackItem();
    this.checkSpecialRouteHandling();
    this.navigationStateService.removeLatestNavigatingBackTo();
    return backRoute;
  }

  getRouteFromLatestNavigatingBackItem(): string {
    switch (this.navigationStateService.getLatestNavigatingBackTo()) {
      case 'InvoiceDetails':
        return `/${INTERACTIVE_INVOICE_ROUTE}/${INTERACTIVE_INVOICE_DETAILS_ROUTE}`;
      case 'ProductDetails':
        return `/${PRODUCTS_ROUTE}/${PRODUCTS_DETAILS_ROUTE}`;
      case 'TariffChange':
        return `/${TARIFF_CHANGE_ROUTE}/${TARIFF_CHANGE_ADDRESS_SELECTION_ROUTE}`;
      case 'Relocation':
        return `/${RELOCATION_ROUTE}/${RELOCATION_CURRENT_ADDRESS_ROUTE}`;
      case 'InvoiceOverview':
        return `/${INTERACTIVE_INVOICE_ROUTE}`;
      case 'PdfInvoiceChange':
        return `/${PDF_INVOICE_CHANGE_ROUTE}/${PDF_INVOICE_CHANGE_ADDRESS_SELECTION_ROUTE}`;
      case 'BankAccountChange':
        return `/${SEPA_INVOICE_CHANGE_ROUTE}/${SEPA_INVOICE_CHANGE_ADDRESS_SELECTION_ROUTE}`;
      case 'InvoiceAddressChange':
        return `/${INVOICE_ADDRESS_CHANGE_ROUTE}/${INVOICE_ADDRESS_CHANGE_ADDRESS_SELECTION_ROUTE}`;
      case 'CustomerDataChange':
        return `/${CUSTOMER_DATA_CHANGE_ROUTE}/${CUSTOMER_DATA_CHANGE_SELECTION_ROUTE}`;
      case 'MoveOut':
        return `/${MOVE_OUT_ROUTE}/${MOVE_OUT_ADDRESS_SELECTION_ROUTE}`;
      case 'VoucherRedeem':
        return `/${VOUCHER_REDEEM_ROUTE}/${VOUCHER_REDEEM_FORM_ROUTE}`;
      case 'Newsletter':
        return `/${PROFILE_ROUTE}/${PROFILE_NEWSLETTER_ROUTE}`;
      case 'Consumption':
        return `/${PRODUCTS_ROUTE}/${PRODUCTS_CONSUMPTION}`;
      case 'Goodies':
        return `/${GOODIE_ROUTE}`;
      case 'UnpaidInvoicesSelection':
        return `/${UNPAID_INVOICES_ROUTE}/${UNPAID_INVOICES_SELECTION_ROUTE}`;
      case 'UnpaidInvoicesOptions':
        return `/${UNPAID_INVOICES_ROUTE}/${UNPAID_INVOICES_PAYMENT_OPTIONS_ROUTE}`;
      case 'PartialPayment':
        return `/${INVOICE_PARTIALPAYMENT_OVERVIEW_ROUTE}`;
      case 'Billinfo':
        return `/${PRODUCTS_ROUTE}/${PRODUCTS_BILLINFO_ROUTE}`;
      case 'SecureOffer':
        return `/${SECURE_OFFER_ROUTE}`;
      case 'ClearingAccountInformationRequest':
        return `/${CLEARING_ACCOUNT_INFORMATION_REQUEST_ROUTE}/${CLEARING_ACCOUNT_INFORMATION_REQUEST_ACCOUNT_SELECTION_ROUTE}`;
      default:
        return '';
    }
  }

  checkSpecialRouteHandling(): void {
    switch (this.navigationStateService.getLatestNavigatingBackTo()) {
      case 'InvoiceDetails':
        this.interactiveInvoicesStateService.keepSessionStorage = true;
        break;
      case 'UnpaidInvoicesOptions':
        this.findPaymentSolutionStateService.keepStorageOnce = true;
        break;
    }
  }

  getProcessLabelKeyFromLatestNavigatingBackItem(): string {
    switch (this.navigationStateService.getLatestNavigatingBackTo()) {
      case 'TariffChange':
        return 'tariffChange.title';
      case 'Relocation':
        return 'relocation.title';
      case 'InvoiceOverview':
        return 'interactive-invoices.entry-slider-card';
      case 'PdfInvoiceChange':
        return 'pdfInvoiceChange.entry';
      case 'BankAccountChange':
        return 'sepaInvoiceChange.entry';
      case 'InvoiceAddressChange':
        return 'invoiceAddress.change.entry';
      case 'CustomerDataChange':
        return 'customerData.change.entry';
      case 'MoveOut':
        return 'move-out.entry';
      case 'VoucherRedeem':
        return 'voucher.redeem.entry';
      case 'Newsletter':
        return 'profile.newsletter.title';
      case 'Consumption':
        return 'consumption.menuItem';
      case 'Goodies':
        return 'goodie.title';
      case 'PartialPayment':
        return 'invoice-partialpayment-overview.entry';
      case 'Billinfo':
        return 'billinfo.group.header';
      case 'SecureOffer':
        return 'secure-offer.wizard.entry';
      default:
        return '';
    }
  }

  switchPortal(queryParam = ''): void {
    sessionStorage.clear();
    this.location.href = this.getSwitchPortalUrl() + queryParam;
  }

  getSwitchPortalUrl(): string {
    return this.navigationStateService.navbarConfig.profile.portalSwitchRoute;
  }

  navigateToB2C(route: string): void {
    if (this.environmentService.getPortalCustomerType() === 'privat') {
      this.router.navigateByUrl(route);
      return;
    }

    this.location.href = `${this.environmentService.getPrivatPortalUri()}/${route}`;
  }

  navigateToB2B(route: string): void {
    if (this.environmentService.getPortalCustomerType() === 'business') {
      this.router.navigateByUrl(route);
      return;
    }

    this.location.href = `${this.environmentService.getBusinessPortalUri()}/${route}`;
  }

  navigateToFeatureToggleOut(processTitle: string): void {
    this.appStorageService.setCurrentFeatureToggledOutInfo({ processTitle });
    this.router.navigateByUrl(`${FEATURE_TOGGLED_OUT_ROUTE}`);
  }
}
